import { History } from 'history';
import { BookingFlowStep } from './constants';
import { goToStep } from './goToStep';
import { trackAddOnRemoved } from './tracking';

export type PickedSelection = {
  /**
   * The hydrated pre-click selection (`book.services`), the same source the modal reads —
   * so the tracked `service_ids` matches the control arm's shape.
   */
  hydratedServices: readonly { id?: number | null }[] | undefined;
  /** Canonical `rs` ids as the page's readers matched them. */
  pickedAddOnIds: readonly string[];
  /** Canonical `ss` ids as the page's readers matched them. */
  pickedMainServiceIds: readonly string[];
};

/**
 * Removes a service from the summary on a step page: add-ons come out of `rs`, main services
 * out of `ss`, and the flow stays on `step`. The last main service is not removable —
 * emptying `ss` fails the remain rule and bounces the user out of the drawer — and an id
 * the URL does not hold is ignored. `removed_service` fires before the push so the session
 * still names the step it happened on.
 */
export function removePickedService(
  history: Pick<History, 'push' | 'replace' | 'location'>,
  placeSlugId: string,
  step: BookingFlowStep,
  serviceId: number,
  { hydratedServices, pickedAddOnIds, pickedMainServiceIds }: PickedSelection,
): void {
  const id = String(serviceId);
  const remainingServiceIds = (hydratedServices ?? []).flatMap((service) =>
    typeof service.id === 'number' && service.id !== serviceId ? [String(service.id)] : [],
  );
  if (pickedAddOnIds.includes(id)) {
    const remaining = pickedAddOnIds.filter((addOnId) => addOnId !== id);
    trackAddOnRemoved(remainingServiceIds);
    goToStep(history, placeSlugId, { type: 'step', step }, { rs: remaining.length > 0 ? remaining.join(',') : null });
    return;
  }
  const remainingMains = pickedMainServiceIds.filter((mainId) => mainId !== id);
  if (remainingMains.length === 0 || remainingMains.length === pickedMainServiceIds.length) return;
  trackAddOnRemoved(remainingServiceIds);
  goToStep(history, placeSlugId, { type: 'step', step }, { ss: remainingMains.join(',') });
}
